import Editor from "@draft-js-plugins/editor"
import React, {createRef, useCallback, useEffect, useState} from "react"
import {CompositeDecorator, convertToRaw, EditorState, RichUtils} from "draft-js"
import styles from "./SpellChecker.module.css";
import ContentState from "draft-js/lib/ContentState"
import classnames from "classnames"
import Suggestions from "./Suggestions"
import Modifier from "draft-js/lib/DraftModifier"
import {stateToHTML} from 'draft-js-export-html'
import {stateFromHTML} from 'draft-js-import-html'
import {checkSpelling, checkTexts, getSeparator, isFilled, regEscapeSymbols, regIndexOf, regLastIndexOf} from "./utils";
import {translation} from "../../../I18n/i18n";
import Controls from "./ToolBar/Controls";
import KnownBulletPoint from "./MarkUp/KnownBulletPoint";
import {
    BULLETS_ARR,
    FIRST_LEVEL_BULLET,
    NEW_LINE_CHAR,
    NUMBERING,
    SECOND_LEVEL_BULLET,
    VERTICAL_LINE_CHAR
} from "../../../applicationConstants";

let Element = (props) => {

    let ref = createRef()
    let [separator, setSeparator] = useState(getSeparator(props.lang))
    let [focus, setFocus] = useState(false)
    let [isActiveMultilevel,setIsActiveMultilevel]=useState(false)

    useEffect(() => {
        // when the editor is not active or focused
        (document.activeElement !== ref.current) && !focus && props.value && handleChange(EditorState.push(editorState,
            !props.freeTextBox ? ContentState.createFromText(props.value) : stateFromHTML(props.value),
            'insert-characters'))

    }, [props.value])

    let updateValue = e => {
        props.freeTextBox ?
            props.onChange(stateToHTML(e.getCurrentContent()))
            : props.onChange(e.getCurrentContent().getPlainText())
    }
    let handleChange = useCallback((_value) => {
        setEditorState(_value)
        // update value, if content changes & change was not triggered by a change in focus/selection
        editorState.getCurrentContent() !== _value.getCurrentContent() && updateValue(_value)
    }, [props.value])

    // check spelling when the hanging to check words changes
    let [toCheck, setToCheck] = useState(null)

    useEffect(() => {
        toCheck && toCheck[0] && checkSpelling(toCheck[0], props.dict, misspells, setMisspells, castOffs)
        toCheck && toCheck[1] && checkSpelling(toCheck[1], props.dict, misspells, setMisspells, castOffs)
    }, [toCheck])

    let handleFocus = () => {
        setFocus(true)
    }

    function checkCurrentCursorFocus() {
        if (isActiveMultilevel) {
            setFocus(true)
        } else {
            setFocus(false)
        }
    }

    let handleBlur = () => {
        checkCurrentCursorFocus()
        // --
        if (cursorPosition && getLastRecast()) {
            checkSpelling(getLastRecast(), props.dict, misspells, setMisspells, castOffs)
        }

        setCursorPosition(null)
    }

    let handleContextMenu = e => {
        e.preventDefault()
        return false
    }

    let [cursorPosition, setCursorPosition] = useState(null)

    let handleCursorMove = () => {
        if (cursorPosition && getLastRecast()) {
            checkSpelling(getLastRecast(), props.dict, misspells, setMisspells, castOffs)
        }

        setCursorPosition(editorState.getSelection())
    }

    let getLastRecast = () => {
        let block = editorState.getCurrentContent().getBlockForKey(cursorPosition.getAnchorKey())

        if (block && block.text) {
            let start = 1 + regLastIndexOf(block.text.substring(0, cursorPosition.anchorOffset), separator)
            let length = regIndexOf(block.text.substring(start), separator)

            let recast = length > 0 ?
                block.text.substring(start, start + length)
                : block.text.substring(start)

            // get the last recast (the word on which the cursor was)
            return regEscapeSymbols(recast)
        }

        return null
    }
    let handlePastedText = (text, html, editorState) => {
        if (text && text !== "") {
            // set words to check from pasted text
            let s = new Set ()
            text.split (separator).reduce ((_, word) => s.add (word), null)
            s.forEach (word => {
                checkSpelling (word, props.dict, misspells, setMisspells, castOffs)
                s.delete (word)
            })
            // update state & value
            mergePastedTextFormatting (text,editorState)
            return "handled";
        }
    }
    /**
     * Returns corresponding selection of pasted text.
     */
    let mergePastedTextFormatting = (text,editorState) => {
        const newContent = Modifier.replaceText(
            editorState.getCurrentContent(),
            editorState.getSelection(),
            // remove Bullets & Numbering from each #_block.text
            removeBulletsAndNumbering(text, editorState.getSelection().getAnchorOffset()!==0)
        );
        const newEditorState = EditorState.push(editorState, newContent,"replace-Text" );
        setEditorState(newEditorState);
    };
    function getCurrentLine(editorState) {
        const selection = editorState.getSelection();
        const block = editorState
            .getCurrentContent()
            .getBlockForKey(selection.getStartKey());
        const start = block.getText().lastIndexOf("\n", selection.getStartOffset() - 1);
        const end = block.getText().indexOf("\n", selection.getEndOffset());
        return block.getText().slice(
            start === -1 ? 0 : start + 1,
            end === -1 ? block.getText().length : end
        );
    }
    function updateCursorPosition(editorState,newEditorState,insertion){
        return EditorState.forceSelection(
            newEditorState,
            newEditorState.getSelection().merge({
                anchorOffset: editorState.getSelection().getAnchorOffset() + insertion.length,
                focusOffset: editorState.getSelection().getFocusOffset() + insertion.length
            })
        );
    }
    function setBulletPointToNewLine(e) {
        const bullets = [FIRST_LEVEL_BULLET, SECOND_LEVEL_BULLET]
        const selection = editorState.getSelection();
        const content=editorState.getCurrentContent();
        e.preventDefault();
        bullets.forEach((bulletPoint) => {
            if (getCurrentLine(editorState).startsWith(bulletPoint)) {
                const newContent = Modifier.insertText(content, selection, bulletPoint);
                const splitContent=Modifier.splitBlock(newContent, selection);
                const newEditorState = EditorState.push(editorState, splitContent, 'split-block');
                const updatedEditorState = updateCursorPosition(newEditorState,newEditorState,bulletPoint)
                setEditorState(updatedEditorState)
            }
        })
    }
    function isCurrentLineStartWithBullet(editorState){
        return(getCurrentLine(editorState).startsWith(FIRST_LEVEL_BULLET)
            || getCurrentLine(editorState).startsWith(SECOND_LEVEL_BULLET))
    }
    function enterKeyTreatment(action){
        if (isCurrentLineStartWithBullet(editorState)) {
            // Add new bullet point on 'Enter' press, if exist in current line
            // check last word on 'Enter' press, if (!freeTextBox)
            setBulletPointToNewLine(action)
            setToCheck(getLastTyping())
            return "handled"
        } else if(props.freeTextBox){
            // check last word on 'Enter' press, if (freeTextBox)
            action.preventDefault()
            return setToCheck(getLastTyping())
        }
        // disable on 'Enter' press, if (entryBox)
        return "not-handled"
    }

    function isAlphaNumericOrSpecialChar(e) {
        const isAlphabetic = /^[a-zA-Z]$/.test(e.key);
        const isNumeric = /^\d$/.test(e.key);
        const isSpecial = /[!@#$%^&*)(+=.<>{}\[\]:;'"|~`_\-]/.test(e.key)
        return (isAlphabetic || isNumeric || isSpecial)
    }

    function onlyBulletPointByDefault(e) {
        return getCurrentLine(editorState).length === 0
            && isAlphaNumericOrSpecialChar(e)
            && !e.ctrlKey
            && props.onlyBulletPoint
    }

    let handleKeyBinding = e => {
        if (anchorEl) {
            hideSuggestions()
        }
        if (onlyBulletPointByDefault(e)) {
            insertBulletPointToEmptyLine(FIRST_LEVEL_BULLET)
        }
        switch (e.keyCode) {
            // Enter key
            case 13:
                return enterKeyTreatment(e)
            // check last word on 'Space' press
            case 32:
                return setToCheck(getLastTyping())
            // otherwise, hide suggestions
            default:
                return hideSuggestions()
        }
    }
    let [misspells, setMisspells] = useState([])
    let [castOffs, setCastOffs] = useState([])

    let [selectedMisspell, setSelectedMisspell] = useState(null)
    let [anchorEl, setAnchorEl] = useState(null)

    let handleClick = e => {
        setAnchorEl(e.currentTarget)
        setSelectedMisspell(e.target.innerText)
    }

    let hideSuggestions = () => {
        setSelectedMisspell(null)
        setAnchorEl(null)
    }

    let markUp = _props => {
        return (
            <span className={styles.misspell} onContextMenu={handleClick}>
                {_props.children}
            </span>
        )
    }

    let misspelledWord = {
        strategy: (content, callback) => {
            let text = content.getText ()

            let matchArr, start
            let exp = new RegExp (`(?<=[^A-Za-z\u00C0-\u00FF]|^)(${misspells.join("|")})(?=[^A-Za-z\u00C0-\u00FF]|$)`, "g")

            while (misspells.length && (matchArr = exp.exec (text)) !== null) {
                start = matchArr.index

                callback (start, start + matchArr[0].length)
            }
        }, component: markUp,
    }

    let recognizedBullets = {
        // recognized bullets decorator
        strategy: (content, callback) => {
            let text = content.getText ()

            let matchArr, start
            let regExp = new RegExp (`^\\S*(?:${[FIRST_LEVEL_BULLET, SECOND_LEVEL_BULLET].join (VERTICAL_LINE_CHAR)})+\\s*`, `gm`)

            while ((matchArr = regExp.exec (text)) !== null) {
                start = matchArr.index

                callback (start, start + matchArr[0].length)
            }
        }, component: KnownBulletPoint,
    }

    let decorators = new CompositeDecorator([misspelledWord, recognizedBullets])

    let [editorState, setEditorState] = useState (() => {
        return !props.value ? EditorState.createEmpty (decorators) : EditorState.createWithContent (stateFromHTML (props.value), decorators)
    })

    useEffect (() => {
        setEditorState (prev => {
            return EditorState.set (prev, {decorator: decorators})
        })
    }, [misspells, castOffs])

    let correctMisspell = (suggestion) => {
        let selection = editorState.getSelection()
        let content = editorState.getCurrentContent()

        let block = content.getBlockForKey(selection.getAnchorKey())

        let lastSpace = regLastIndexOf(block.text.substring(0, selection.anchorOffset), separator)
        let start = lastSpace === selection.anchorOffset ? lastSpace - selectedMisspell.length : 1 + lastSpace

        let newContent = Modifier.replaceText (content, selection.merge ({
            anchorKey: selection.anchorKey,
            anchorOffset: start,
            focusKey: selection.focusKey,
            focusOffset: start + (selectedMisspell ? selectedMisspell.length : 0),
        }), suggestion)

        setEditorState (EditorState.push (editorState, newContent, 'spellcheck-change'))
        hideSuggestions ()
    }

    let ignoreMisspell = () => {
        let caseOff = regEscapeSymbols(selectedMisspell)
        setCastOffs(v => {
            return v.includes(caseOff) ? v : v.concat(caseOff)
        })
        misspells.splice(misspells.indexOf(caseOff), 1)
        hideSuggestions()
    }

    let [addToDictLabel, setAddToDictLabel] = useState(translation(props.lang, "Add to dictionary"))

    let addToDictionary = () => {
        let newTerm = regEscapeSymbols(selectedMisspell)
        setAddToDictLabel(`${translation(props.lang, "Adding")} ...`)

        props.addToDictionary && props.addToDictionary(newTerm)
            .then(r => {
                if (r && r.ok) {
                    setMisspells(v => v.filter(el => el !== newTerm))
                    setCastOffs(v => v.concat(newTerm))
                } else {
                    props.notify("Failed to add to dictionary !", "error")
                    hideSuggestions()
                    setAddToDictLabel(translation(props.lang, "Add to dictionary"))
                }
            })
            .then(() => {
                hideSuggestions()
                setAddToDictLabel(translation(props.lang, "Add to dictionary"))
            })
    }
    let getLastTyping = () => {
        let selection = editorState.getSelection()
        let block = editorState.getCurrentContent().getBlockForKey(selection.getAnchorKey())

        let leftWords = block.text.substring(0, selection.anchorOffset).split(separator)
        let rightWords = block.text.substring(selection.anchorOffset).split(separator)

        return [leftWords[leftWords.length - 1], rightWords[0]]
    }

    let toggleInlineStyle = style => {
        handleChange(
            RichUtils.toggleInlineStyle(
                editorState,
                style
            )
        )
    }

    let toggleBlockType = type => {
        handleChange(
            RichUtils.toggleBlockType(
                editorState,
                type
            )
        )
    }

    // new check all, on opening / on dictionary change
    let resetSpellCheck = () => {
        isFilled(props.value) &&
            checkTexts(editorState.getCurrentContent().getPlainText(), props.lang, props.dict)
                .then(arr => setMisspells(arr))
    }
    useEffect(() => resetSpellCheck(), [props.dict])

    // reset all, on opening / on language change
    let resetAll = () => {
        setSeparator(getSeparator(props.lang))
        setCastOffs([])
        setMisspells([])
    }
    useEffect(() => resetAll(), [props.lang])

    let getSelectedBlocks = () => {
        let contentState = editorState.getCurrentContent()
        let currentSelection = editorState.getSelection()

        let startKey = currentSelection.getStartKey()
        let endKey = currentSelection.getEndKey()

        let isSameBlock = startKey === endKey
        let startingBlock = contentState.getBlockForKey(startKey)
        let selectedBlocks = [startingBlock]

        if (!isSameBlock) {
            let blockKey = startKey;

            while (blockKey !== endKey) {
                let nextBlock = contentState.getBlockAfter(blockKey)

                selectedBlocks.push(nextBlock)
                blockKey = nextBlock.getKey()
            }
        }

        return selectedBlocks;
    }

    let asContentBlocks = (contentArr) => {
        return contentArr.map (el => {
            return [el.key, el.text]
        });
    }

    let getSafeContentBlocks = () => {
        let rawContentState = convertToRaw(editorState.getCurrentContent())
        return asContentBlocks(rawContentState.blocks)
    }
    function getTextFromCurrentBlock(currentBlockText,currentBlockKey,toReplace,insertion){
        let hasToReplace = toReplace
            .find(_el => currentBlockText.startsWith(_el))
        let isInSelection = getSelectedBlocks()
            .every(_el => _el.getKey() !== currentBlockKey)
        if (isInSelection) {
            return currentBlockText
        } else if (hasToReplace) {
            return currentBlockText.replace(hasToReplace, insertion)
        }
        return insertion + currentBlockText
    }
    function isExistingBlock(){
        return getCurrentLine(editorState).length>0
            && getCurrentLine(editorState).length!==FIRST_LEVEL_BULLET.length
            && getCurrentLine(editorState).length!==SECOND_LEVEL_BULLET.length
            || !editorState.getSelection().isCollapsed()
    }
    function isBulletPointExistInNewLine(insertion){
        return getCurrentLine(editorState).length===insertion.length || insertion.length===0
    }
    function replaceRemovedBulletPoint(editorState,insertion){
        const editorStateAfterRemove=removeElementBeforeCursor(editorState,insertion)
        const content=editorStateAfterRemove.getCurrentContent()
        const selection=editorStateAfterRemove.getSelection()
        const newContent = Modifier.replaceText(content,selection,insertion)
        const newEditorState = EditorState.push(editorStateAfterRemove, newContent, 'split-block')
        const updatedEditorState = updateCursorPosition(editorStateAfterRemove,newEditorState,insertion)
        setEditorState(updatedEditorState)
    }
    const isBulletPointBeforeCursor = (editorState, bulletPoint) => {
        const contentState = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const cursorPosition = selection.getAnchorOffset();
        const block = contentState.getBlockForKey(selection.getStartKey());
        const blockText = block.getText();
        const bulletPointIndex = blockText.indexOf(bulletPoint);
        return bulletPointIndex !== -1 && bulletPointIndex < cursorPosition;
    };
    function getStartRemoveCursorPosition(insertion,startOffset){
        if(insertion.length!==0){
            return startOffset - insertion.length
        }
        return startOffset - getCurrentLine(editorState).length
    }
    function getRangeOfTextToBeRemoved(insertion){
        const selection = editorState.getSelection();
        const startOffset = selection.getStartOffset();
        const currentBulletPoint=getCurrentLine(editorState)
        if(isBulletPointBeforeCursor(editorState,currentBulletPoint)){
            return selection.merge({
                anchorOffset:getStartRemoveCursorPosition(insertion,startOffset),
                focusOffset: startOffset,
            });
        }
        return selection.merge({
            anchorOffset: startOffset,
            focusOffset: startOffset + getCurrentLine(editorState).length,
        });
    }
    function removeElementBeforeCursor(editorState,insertion) {
        const content = editorState.getCurrentContent();
        // Get the range of text to be removed
        const removedTextRange=getRangeOfTextToBeRemoved(insertion)
        // Remove the character from the content state
        const newContent = Modifier.removeRange(content, removedTextRange, 'backward');
        // Update the editor state
        return EditorState.push(editorState,newContent,'remove-range');
    }
    function insertBulletPointToEmptyLine(insertion){
        const newContent = Modifier.insertText(editorState.getCurrentContent(), editorState.getSelection(), insertion)
        const newEditorState = EditorState.push(editorState, newContent, 'insert-characters')
        const updatedEditorState = updateCursorPosition(editorState,newEditorState,insertion)
        setEditorState(updatedEditorState)
    }
    let insertToAllSelectedBlocks = (selection, insertion, toReplace) => {
        let newText = ""
        getSafeContentBlocks().forEach((el, i) => {
            if (Array.isArray(el) && el.length === 2) {
                let currentBlockKey = el[0]
                let currentBlockText = el[1]
                newText += (0 !== i) ? NEW_LINE_CHAR : ``
                if (isExistingBlock()) {
                    newText += getTextFromCurrentBlock(currentBlockText,currentBlockKey,toReplace,insertion)
                    let newContent = ContentState.createFromText(newText);
                    let newEditorState = EditorState.push(editorState, newContent, 'split-block')
                    setEditorState(newEditorState)
                }else if(isBulletPointExistInNewLine(insertion)){
                    replaceRemovedBulletPoint(editorState,insertion)
                }
                else if(getCurrentLine(editorState).length===0) {
                    insertBulletPointToEmptyLine(insertion)
                }
            }
        })
    }
    let toggleMultilevel = (style, options) => {
        let selection = getSelectedBlocks ()
        insertToAllSelectedBlocks (selection, options[style], [options["bullet1"], options["bullet2"]])
    }
    /**
     * Removes numbering and merges source bullet format with editor bullets format.
     *  -> Unrecognized bullets are merged with the top-level editor bullets.
     *  -> Only recognized second level chips will be kept as is.
     */
    function onlyBulletPointPasteText(text, index) {
        return props.onlyBulletPoint
            && !text.startsWith(FIRST_LEVEL_BULLET) && !text.startsWith(SECOND_LEVEL_BULLET)
            && !((getCurrentLine(editorState).startsWith(FIRST_LEVEL_BULLET) || getCurrentLine(editorState).startsWith(SECOND_LEVEL_BULLET)) && index === 0)
    }

    function pushPastedTextAfterTreatment(resultText, pastedText, listOfText, index) {
        if (resultText !== '') {
            if (onlyBulletPointPasteText(resultText, index)) {
                resultText = FIRST_LEVEL_BULLET + pastedText
                listOfText.push(resultText)
            } else {
                listOfText.push(resultText)
            }
        }
    }
    let removeBulletsAndNumbering = (text, skipFirstLine) => {
        let arr = []
        text.split(NEW_LINE_CHAR).forEach((_line, _index) => {
            let str = _line
            if (!props.onlyText) {
                if (skipFirstLine && _index === 0) {
                    str = replaceMatches([FIRST_LEVEL_BULLET, SECOND_LEVEL_BULLET].join(VERTICAL_LINE_CHAR), str, ``)
                } else {
                    str = replaceMatches(NUMBERING, str, ``)
                }
                str = replaceMatches(BULLETS_ARR.join(VERTICAL_LINE_CHAR), str, (skipFirstLine && _index === 0) ? `` : FIRST_LEVEL_BULLET)
            } else {
                BULLETS_ARR.push(FIRST_LEVEL_BULLET, SECOND_LEVEL_BULLET)
                str = replaceMatches(BULLETS_ARR.join(VERTICAL_LINE_CHAR), str, ``)
            }
            pushPastedTextAfterTreatment(str, _line, arr, _index)
        })
        return arr.join(NEW_LINE_CHAR)
    }

    let replaceMatches = (searchVal, text, replacement) => {
        if (text && text !== "") {
            let regExp = new RegExp (`^\\S*(?:${searchVal})+\\s*`, `gm`)
            return text.replaceAll (regExp, replacement)
        }
        return text
    }

    // undo - redo
    let handleUndo = () => {
        setEditorState (prev => EditorState.undo (prev))
    }

    let handleRedo = () => {
        setEditorState (prev => EditorState.redo (prev))
    }

    let toggleUndoRedo = (action) => {
        if (action.toLowerCase () === "undo") {
            return handleUndo ()
        }
        if (action.toLowerCase () === "redo") {
            return handleRedo ()
        }
    }
    useEffect(() => {
        if (props.onlyBulletPoint && getCurrentLine(editorState).length === 0 && focus) {
            insertBulletPointToEmptyLine(FIRST_LEVEL_BULLET)
        }
    }, [focus])
    let placeholder = (
        <span
            className={styles.placeholder}>{!isFilled(props.value) && !focus && props.placeholder}
        </span>
    )
    let controlFeedback = props.isInvalid && <span className={styles.feedback}>{props.controlFeedback}</span>

    return <div className={classnames(styles.root, [
        focus && props.toolbar && styles.focus
    ])}>

        { props.multilevelTool && <Controls
            editorState={ editorState }
            toggleInlineStyle={ toggleInlineStyle }
            toggleBlockType={ toggleBlockType }
            toggleUndoRedo={ toggleUndoRedo }
            toggleMultilevel={ toggleMultilevel }
            onlyBulletPoint={props.onlyBulletPoint}
            setIsActiveMultilevel={setIsActiveMultilevel}
            options={ {
                inline: props.toolbar,
                blockType: props.toolbar,
                undoRedo: props.multilevelTool,
                multilevel: props.multilevelTool
            } }
            isDisabled={props.isDisabled}
        /> }

        <div
            className={ classnames (styles.input, [props.freeTextBox && styles.free, props.isDisabled && styles.disabled, props.isInvalid && styles.invalid, props.toolbar || props.multilevelTool && styles.radius, focus && !props.toolbar && !props.multilevelTool && styles.focus]) }
            onContextMenu={ handleContextMenu }
            onClick={ () => {
                ref.current.focus ()
                handleCursorMove ()
            } }>
            { placeholder }
            <Editor ref={ ref }
                    editorState={ editorState }
                    onChange={ handleChange }
                    onFocus={ handleFocus }
                    onBlur={ handleBlur }
                    handlePastedText={ handlePastedText }
                    keyBindingFn={ handleKeyBinding }
                    readOnly={ props.isDisabled }
                    stripPastedStyles={ true }
            />
        </div>
        {controlFeedback}
        <Suggestions selectedMisspell={selectedMisspell}
            anchorEl={anchorEl}
            onClose={hideSuggestions}
            correctMisspell={correctMisspell}
            ignoreMisspell={ignoreMisspell}
            addToDictionary={addToDictionary}
            addToDictLabel={addToDictLabel}
            lang={props.lang}
        />
    </div>
}

export default Element;